/**
 * Schema.org utility functions for generating structured data
 * This helps with SEO by providing search engines with structured information about our pages
 */

type SchemaType = 
  | 'WebSite' 
  | 'WebPage' 
  | 'Organization'
  | 'BreadcrumbList'
  | 'FAQPage'
  | 'Article'
  | 'Product'
  | 'Service'
  | 'ItemList'
  | 'WebApplication';

/**
 * Generate schema.org structured data for different page types
 * @param pageType The type of page we're generating schema for
 * @returns Array of schema.org structured data objects
 */
export function generateSchemaByPageType(pageType: string): any[] {
  // Common schemas that should be on most pages
  const baseSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "PetDocument",
      "url": "https://petdocument.com",
      "description": "Securely store and manage your pet's health records, vaccination history, and medical documents in one place.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://petdocument.com/search?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "PetDocument",
      "url": "https://petdocument.com",
      "logo": "https://petdocument.com/logo.png",
      "sameAs": [
        "https://facebook.com/petdocument",
        "https://twitter.com/petdocument",
        "https://instagram.com/petdocument"
      ]
    }
  ];

  // Page-specific schema additions
  const pageSpecificSchemas: Record<string, any[]> = {
    home: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "PetDocument - Pet Document Management System",
        "description": "Store and organize your pet's medical records, vaccination history, medications, and important documents in one secure digital platform.",
        "url": "https://petdocument.com/",
        "isPartOf": {
          "@type": "WebSite",
          "url": "https://petdocument.com"
        }
      }
    ],
    features: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Features - PetDocument",
        "description": "Discover the key features of PetDocument - secure document storage, vaccination tracking, and health records management for your pets.",
        "url": "https://petdocument.com/features",
        "isPartOf": {
          "@type": "WebSite",
          "url": "https://petdocument.com"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "PetDocument Features",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Secure Document Storage",
            "description": "Keep all your pet's documents secure and accessible anytime, anywhere."
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Vaccination Tracking",
            "description": "Never miss an important vaccination with reminders and history tracking."
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Health Records",
            "description": "Store complete medical histories, vet visit notes, and prescriptions."
          }
        ]
      }
    ],
    pricing: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Pricing - PetDocument",
        "description": "Choose the right PetDocument plan for your needs. Compare our Free and Premium plans.",
        "url": "https://petdocument.com/pricing",
        "isPartOf": {
          "@type": "WebSite",
          "url": "https://petdocument.com"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "PetDocument Premium",
        "description": "Advanced pet document management with unlimited storage and priority support",
        "offers": {
          "@type": "Offer",
          "price": "9.99",
          "priceCurrency": "USD",
          "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0],
          "availability": "https://schema.org/InStock"
        }
      }
    ],
    about: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "About PetDocument",
        "description": "Learn about PetDocument's mission to simplify pet document management for pet owners.",
        "url": "https://petdocument.com/about",
        "isPartOf": {
          "@type": "WebSite",
          "url": "https://petdocument.com"
        }
      }
    ],
    contact: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Contact PetDocument",
        "description": "Contact the PetDocument team for support, feedback, or partnership inquiries.",
        "url": "https://petdocument.com/contact",
        "isPartOf": {
          "@type": "WebSite",
          "url": "https://petdocument.com"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact PetDocument",
        "description": "Contact information for PetDocument support and inquiries",
        "url": "https://petdocument.com/contact"
      }
    ],
    faq: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Frequently Asked Questions - PetDocument",
        "description": "Find answers to common questions about PetDocument's features, pricing, and account management.",
        "url": "https://petdocument.com/faq",
        "isPartOf": {
          "@type": "WebSite",
          "url": "https://petdocument.com"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is PetDocument?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "PetDocument is a secure platform for storing and managing your pet's documents, including health records, vaccination certificates, and important information."
            }
          },
          {
            "@type": "Question",
            "name": "How much does PetDocument cost?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "PetDocument offers a free plan with basic features and a premium plan starting at $9.99/month with advanced features like unlimited storage and priority support."
            }
          }
        ]
      }
    ]
  };

  // Return combined schemas
  return [...baseSchemas, ...(pageSpecificSchemas[pageType] || [])];
}
